import { Button, Grid, Tab, Tabs, Typography, Alert } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { ALLOTMENT_TABS, FREEDAY_BID_TYPE } from "../../util/bid/bidConstants";
import { isObjectEmpty } from "../../util/commonUtils";
import ReliefAllotment from "./ReliefAllotment";
import ReliefAllotmentDialog from "./ReliefAllotmentDialog";
import StandardAllotment from "./StandardAllotment";
import StandardAllotmentDialog from "./StandardAllotmentDialog";
import StarsCalendar from "./StarsCalendar";
import {
  buildPayloadForAllotmentCalculation,
  getReliefEmployeesCount,
  getStandardEmployeesCount,
  getTotalStandardAllotments,
  calcReliefAllotmentInitialState,
  calcStandardAllotmentInitialState,
} from "../../util/bid/freedayBidUtils";
import ConfirmationDialog from "../fundamentals/ConfirmationDialog";
import { calculateFreedayAllotments } from "../../api/bid";
import SuccessDialog from "../fundamentals/SuccessDialog";
import { validateReliefAllotments, validateStandardAllotments } from "../../util/bid/freedayValidation";
import PageLoading from "../PageLoading";
import { getDayStateForReliefAllotment, getDayStateForStandardAllotment } from "../../util/calendar";
import { isAwardedBid, isClosedBid, isDeletedBid } from "../../util/bid/bidCommon";

const ManageFreedayAllotments = (props) => {
  const [isAllotmentDialogOpen, setIsAllotmentDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTab, setSelectedTab] = useState(ALLOTMENT_TABS.STANDARD);
  // eslint-disable-next-line
  const [totalStandardEmployees, setTotalStandardEmployees] = useState(0);
  const [totalReliefEmployees, setTotalReliefEmployees] = useState(0);

  const [isRecalculateDialogOpen, setIsRecalculateDialogOpen] = useState(false);
  const [isRecalculateLoading, setIsRecalculateLoading] = useState(false);
  const [isRecalculateErrorDialogOpen, setIsRecalculateErrorDialogOpen] = useState(false);
  const [recalculateError, setRecalculateError] = useState("");

  const [standardAllotmentErrors, setStandardAllotmentErrors] = useState("");
  const [reliefAllotmentErrors, setReliefAllotmentErrors] = useState("");

  useEffect(() => {
    setTotalStandardEmployees(getStandardEmployeesCount(props.bidGroup));
    setTotalReliefEmployees(getReliefEmployeesCount(props.bidGroup));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.bidGroup]);

  useEffect(() => {
    if (props.schedMonth !== "" && props.schedYear !== "") {
      setStandardAllotmentErrors(
        validateStandardAllotments(
          totalStandardEmployees,
          getTotalStandardAllotments(props.standardDayState),
          props.schedMonth,
          props.schedYear
        )
      );
      setReliefAllotmentErrors(
        validateReliefAllotments(totalReliefEmployees, props.calcReliefAllotments, props.schedMonth, props.schedYear)
      );
    }
  }, [
    totalStandardEmployees,
    totalReliefEmployees,
    props.standardDayState,
    props.calcReliefAllotments,
    props.bidGroup,
    props.schedMonth,
    props.schedYear,
  ]);

  const handleAllotmentDialogOpen = (dayState) => {
    if (dayState.date && (dayState.amCount > 0 || dayState.amOrPmCount > 0)) {
      setSelectedDate(dayState.date);
      setIsAllotmentDialogOpen(true);
    }
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const handleRecalculateDialogRecalculateClick = () => {
    setIsRecalculateLoading(true);
    calculateFreedayAllotments(
      buildPayloadForAllotmentCalculation(
        props.bid,
        props.schedYear,
        props.schedMonth,
        totalStandardEmployees,
        totalReliefEmployees
      )
    )
      .then((res) => res.json())
      .then((response) => {
        props.setCalcStandardAllotments(response.calcStandardAllotments);
        props.setCalcReliefAllotments(response.calcReliefAllotments);
        props.setIsSchedMonthOrYearChanged(false);
        props.setStandardDayState(
          getDayStateForStandardAllotment(props.schedYear, props.schedMonth, response.calcStandardAllotments)
        );
        props.setReliefDayState(
          getDayStateForReliefAllotment(props.schedYear, props.schedMonth, response.calcReliefAllotments)
        );
        setIsRecalculateDialogOpen(false);
        setIsRecalculateLoading(false);
      })
      .catch((err) => {
        props.setCalcStandardAllotments(calcStandardAllotmentInitialState);
        props.setCalcReliefAllotments(calcReliefAllotmentInitialState);
        setRecalculateError("Error. Something went wrong");
        setIsRecalculateErrorDialogOpen(true);
        setIsRecalculateDialogOpen(false);
        setIsRecalculateLoading(false);
        props.setStandardDayState([]);
        props.setReliefDayState([]);
      });
  };

  const renderBidEffectiveDatesChanged = () => {
    return (
      <Grid item xs={12}>
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          The Bid Effective Month/Year has changed. Please click Recalculate to update the allotments.
        </Alert>
      </Grid>
    );
  };

  const renderBidGroupAndAllotmentsCountsMismatch = (standardRelief) => {
    return (
      <Grid item xs={12}>
        <Alert severity={isClosedBid(props.bid.status) ? "error" : "warning"} sx={{ borderRadius: 0 }}>
          {standardRelief === FREEDAY_BID_TYPE.STANDARD &&
            `Not enough allotments for Standard Employees. Please update the allotments manually or by clicking Recalculate.`}
          {standardRelief === FREEDAY_BID_TYPE.RELIEF &&
            `The number of Relief Employees has changed. Please click Recalculate to update the allotments.`}
        </Alert>
      </Grid>
    );
  };

  return (
    <>
      {props.isBidAllotmentsLoading || props.isBidGroupLoading ? (
        <PageLoading />
      ) : (
        <div data-testid="manage-allotments">
          {!!validateReliefAllotments(
            getReliefEmployeesCount(props.bidGroup),
            props.calcReliefAllotments,
            props.schedMonth,
            props.schedYear
          ) && renderBidGroupAndAllotmentsCountsMismatch(FREEDAY_BID_TYPE.RELIEF)}
          {!!validateStandardAllotments(
            getStandardEmployeesCount(props.bidGroup),
            getTotalStandardAllotments(props.standardDayState),
            props.schedMonth,
            props.schedYear
          ) && renderBidGroupAndAllotmentsCountsMismatch(FREEDAY_BID_TYPE.STANDARD)}
          {props.isSchedMonthOrYearChanged && renderBidEffectiveDatesChanged()}
          <Grid container sx={{ border: "solid 1px", borderColor: "secondary.swaGray2" }}>
            <Grid container sx={{ padding: "10px" }}>
              <Grid item xs={10}>
                <Typography
                  variant="h6"
                  sx={{ textAlign: "left", marginTop: "10px", marginLeft: "10px", fontWeight: "bold" }}
                >
                  {props.bid.station} | {props.bid.workgroup}
                  {props.schedMonth !== "" &&
                    props.schedYear !== "" &&
                    ` | ${props.schedYear} ${props.schedMonth.substr(0, 3)}`}
                  {props.bidName ? ` | ${props.bidName}` : ""}
                </Typography>
              </Grid>
              <Grid item xs={2} container justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => {
                    setIsRecalculateDialogOpen(true);
                  }}
                  disabled={
                    props.schedMonth === "" ||
                    props.schedYear === "" ||
                    props.bidGroup.length === 0 ||
                    isAwardedBid(props.bid.status) ||
                    isDeletedBid(props.bid.status)
                  }
                >
                  Recalculate
                </Button>
              </Grid>
            </Grid>
            <Tabs
              value={selectedTab}
              onChange={handleChangeTab}
              sx={{
                width: "inherit",
                margin: "auto",
                bgcolor: "secondary.swaLightestBlue",
              }}
              centered
            >
              <Tab sx={{ fontWeight: "bold" }} label={"STANDARD"} value={ALLOTMENT_TABS.STANDARD}></Tab>
              <Tab sx={{ fontWeight: "bold" }} label={"RELIEF"} value={ALLOTMENT_TABS.RELIEF}></Tab>
            </Tabs>

            {selectedTab === ALLOTMENT_TABS.STANDARD && (
              <>
                <Grid container sx={{ backgroundColor: "secondary.swaLightestBlue" }}>
                  <Grid item xs={6}>
                    <Grid container spacing={2} sx={{ padding: "10px" }}>
                      <Grid item>Total Employees: </Grid>
                      <Grid item sx={{ color: standardAllotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                        {totalStandardEmployees === 0 ? "NA" : totalStandardEmployees}
                      </Grid>
                      <Grid item>Total Allotments:</Grid>

                      <Grid item sx={{ color: standardAllotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                        {totalStandardEmployees === 0 ? "NA" : getTotalStandardAllotments(props.standardDayState)}
                        {/* Allotments to be NA if TotalEmps are 0 */}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={6}>
                    <Grid container spacing={2} justifyContent="flex-end" sx={{ padding: "10px" }}>
                      <Grid item> Calculated Daily Allotments: </Grid>
                      <Grid item sx={{ fontWeight: "bold" }}>
                        {isObjectEmpty(props.calcStandardAllotments)
                          ? "NA"
                          : props.calcStandardAllotments.amOrPmCount > 0
                          ? `${props.calcStandardAllotments.amOrPmCount} AM OR PM`
                          : `${
                              props.calcStandardAllotments.amCount +
                              props.calcStandardAllotments.eitherCount +
                              props.calcStandardAllotments.pmCount
                            } 
                    (${props.calcStandardAllotments.amCount} AM, ${props.calcStandardAllotments.eitherCount} EITHER, ${
                              props.calcStandardAllotments.pmCount
                            } PM)`}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                {props.bidGroup?.length === 0 || props.schedMonth === "" || props.schedYear === "" ? (
                  <Grid
                    container
                    sx={{ minHeight: "280px", color: "secondary.swaBlue", padding: "1rem", textAlign: "left" }}
                  >
                    <Grid item xs={12} sx={{ color: "primary.swaBlue" }}>
                      <Grid>
                        <Typography sx={{ fontWeight: "bold" }}>
                          Please select the Freeday bid month + year and load the Bid Group to view the allotments.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container>
                    <StarsCalendar
                      dayState={props.standardDayState}
                      DayComponent={StandardAllotment}
                      handleDialogOpen={handleAllotmentDialogOpen}
                      isLoading={props.isBidAllotmentsLoading || props.isBidGroupLoading}
                    />
                  </Grid>
                )}
              </>
            )}
            {selectedTab === ALLOTMENT_TABS.RELIEF && (
              <>
                <Grid container sx={{ backgroundColor: "secondary.swaLightestBlue" }}>
                  <Grid item xs={6}>
                    <Grid container spacing={2} sx={{ padding: "10px" }}>
                      <Grid item>Total Employees:</Grid>
                      <Grid item sx={{ color: reliefAllotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                        {totalReliefEmployees === 0 ? "NA" : totalReliefEmployees}
                      </Grid>
                      {props.calcReliefAllotments.amOrPmCount === 1 ? (
                        <>
                          <Grid item>Daily Max: </Grid>
                          <Grid item sx={{ color: reliefAllotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                            {props.calcReliefAllotments.amOrPmCount}
                          </Grid>
                        </>
                      ) : props.calcReliefAllotments.amCount === 1 && props.calcReliefAllotments.pmCount === 1 ? (
                        <>
                          <Grid item>Max AM:</Grid>
                          <Grid item sx={{ color: reliefAllotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                            1
                          </Grid>

                          <Grid item>Max PM:</Grid>
                          <Grid item sx={{ color: reliefAllotmentErrors ? "primary.swaRed" : "", fontWeight: "bold" }}>
                            1
                          </Grid>
                        </>
                      ) : (
                        <></>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
                {props.bidGroup?.length === 0 || props.schedMonth === "" || props.schedYear === "" ? (
                  <Grid
                    container
                    sx={{ minHeight: "280px", color: "secondary.swaBlue", padding: "1rem", textAlign: "left" }}
                  >
                    <Grid item xs={12} sx={{ color: "primary.swaBlue" }}>
                      <Grid>
                        <Typography sx={{ fontWeight: "bold" }}>
                          Please select the Freeday bid month + year and load the Bid Group to view the allotments.
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container>
                    <StarsCalendar
                      dayState={props.reliefDayState}
                      DayComponent={ReliefAllotment}
                      handleDialogOpen={handleAllotmentDialogOpen}
                      isLoading={props.isBidAllotmentsLoading || props.isBidGroupLoading}
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
          {selectedTab === ALLOTMENT_TABS.STANDARD ? (
            <Grid sx={{ border: "40px" }}>
              <StandardAllotmentDialog
                isAllotmentDialogOpen={isAllotmentDialogOpen}
                standardDayState={props.standardDayState}
                date={selectedDate}
                setStandardDayState={props.setStandardDayState}
                setIsAllotmentDialogOpen={setIsAllotmentDialogOpen}
                setCalcStandardAllotments={props.setCalcStandardAllotments}
                bid={props.bid}
              />
            </Grid>
          ) : (
            <ReliefAllotmentDialog
              isAllotmentDialogOpen={isAllotmentDialogOpen}
              reliefDayState={props.reliefDayState}
              date={selectedDate}
              setReliefDayState={props.setReliefDayState}
              setIsAllotmentDialogOpen={setIsAllotmentDialogOpen}
              setCalcReliefAllotments={props.setCalcReliefAllotments}
              bid={props.bid}
            />
          )}
          {isRecalculateDialogOpen && (
            <ConfirmationDialog
              isDialogOpen={isRecalculateDialogOpen}
              title={"Recalculate Allotments"}
              message={"You are recalculating both the Standard and Relief daily allotments."}
              hasWarning={true}
              warningMessage={"This will override any edits made to the allotments."}
              yesTitle={"Recalculate"}
              noTitle={"Cancel"}
              handleYesButtonClicked={handleRecalculateDialogRecalculateClick}
              handleNoButtonClicked={() => {
                setIsRecalculateDialogOpen(false);
              }}
              isYesLoading={isRecalculateLoading}
            />
          )}
          {isRecalculateErrorDialogOpen && (
            <SuccessDialog
              isSuccessDialogOpen={isRecalculateErrorDialogOpen}
              title="Recalculate Error"
              hasError={true}
              error={recalculateError}
              handleOKClick={() => {
                setIsRecalculateErrorDialogOpen(false);
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

ManageFreedayAllotments.propTypes = {
  bid: PropTypes.object.isRequired,
  bidGroup: PropTypes.array.isRequired,
  schedMonth: PropTypes.string.isRequired,
  schedYear: PropTypes.string.isRequired,
  isBidAllotmentsLoading: PropTypes.bool.isRequired,
  isBidGroupLoading: PropTypes.bool.isRequired,
  calcStandardAllotments: PropTypes.object.isRequired,
  setCalcStandardAllotments: PropTypes.func.isRequired,
  standardDayState: PropTypes.array.isRequired,
  setStandardDayState: PropTypes.func.isRequired,
  calcReliefAllotments: PropTypes.object.isRequired,
  setCalcReliefAllotments: PropTypes.func.isRequired,
  reliefDayState: PropTypes.array.isRequired,
  setReliefDayState: PropTypes.func.isRequired,
  isSchedMonthOrYearChanged: PropTypes.bool.isRequired,
  setIsSchedMonthOrYearChanged: PropTypes.func.isRequired,
  bidName: PropTypes.string,
};

export default ManageFreedayAllotments;
